import compact from "lodash/compact";
import isEmpty from "lodash/isEmpty";
import isNil from "lodash/isNil";
import { BookingDetailItem, BookingDetailItemAction } from "PFApp/booking/components/booking_detail_item";
import {
  DetailsPanelSource,
  DetailsPanelType,
  useDetailsPanelsActions
} from "PFApp/booking/components/details_panel";
import { useCanOpenDetailsPanelOfType } from "PFApp/booking/components/details_panel/hooks";
import { ActionType } from "PFApp/booking/query_params_handler.utils";
import useBookingModulePermissions from "PFApp/use_booking_module_permissions";
import { Button } from "PFComponents/button";
import { LoadingDots } from "PFComponents/loading_dots";
import { Typography } from "PFComponents/typography";
import { utc } from "PFCore/helpers/date";
import { getProfileName } from "PFCore/helpers/profile";
import { useIsUnbiasedMode } from "PFCore/hooks";
import { useCurrentAccount } from "PFCore/hooks/queries/account";
import { useUnbiasedProfileName } from "PFCore/hooks/use_unbiased_profile_name";
import { Activity, Profile } from "PFCore/types";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";

import css from "../../detail_view.module.scss";
import { BookingDetailSidePanelProfile } from "../booking_detail_side_panel_context/booking_detail_side_panel_context.types";

type BookingDetailItemWorkforceMemberProps = {
  profile?: BookingDetailSidePanelProfile;
  source?: DetailsPanelSource;
  activity?: Activity;
};

export const BookingDetailItemWorkforceMember = ({
  profile,
  source,
  activity
}: BookingDetailItemWorkforceMemberProps) => {
  const {
    data: { config }
  } = useCurrentAccount();
  const navigate = useNavigate();
  const { t } = useTranslation(["translation", "bookingModule"]);

  const { openDetailsPanel } = useDetailsPanelsActions();
  const canOpenDetailsPanelOfType = useCanOpenDetailsPanelOfType();
  const { isEnabled: isBMEnabled, canProfileCreateBooking } = useBookingModulePermissions();
  const canViewInBookingModule = canProfileCreateBooking(profile?.id);

  const isUnbiasedMode = useIsUnbiasedMode(activity?.reviewers);
  const { getUnbiasedProfileName } = useUnbiasedProfileName();

  if (!!profile && isUnbiasedMode) {
    return (
      <div className={css.actionableContainer}>
        <BookingDetailItem
          label={t("bookingModule:details.workforceMember")}
          value={
            <Typography variant="bodyBold">
              {getUnbiasedProfileName(profile as Profile, isUnbiasedMode)}
            </Typography>
          }
        />
      </div>
    );
  }

  if (isNil(profile)) {
    return (
      <div className={css.actionableContainer}>
        <BookingDetailItem label={t("bookingModule:details.workforceMember")} value={<LoadingDots />} />
      </div>
    );
  }

  const openProfileSidePanel = (id: number) => {
    openDetailsPanel({
      type: DetailsPanelType.Profile,
      id
    });
  };

  const profileNameElement = <Typography variant="bodyBold">{getProfileName(profile)}</Typography>;
  const isSuspended = !!profile.suspendedAt;

  const showInProjectsTab: BookingDetailItemAction = {
    icon: "folder",
    onClick: () => {
      const params = new URLSearchParams({
        action: String(ActionType.ShowInTab),
        mode: "projects",
        workforceMemberId: String(profile.id)
      });
      navigate({ pathname: "/booking/overview", search: params.toString() }, { replace: true });
    },
    tooltip: t("bookingModule:rowsSidePanel.rowMenu.showInTab.workforce")
  };

  const showInWorkforceTab: BookingDetailItemAction = {
    icon: source === DetailsPanelSource.BookingModuleProjects ? "profiles" : "booking",
    onClick: () => {
      const params = new URLSearchParams({
        action: String(ActionType.ShowInTab),
        mode: "workforce",
        workforceMemberId: String(profile.id)
      });
      navigate({ pathname: "/booking/overview", search: params.toString() }, { replace: true });
    },
    tooltip: t("bookingModule:rowsSidePanel.rowMenu.showInTab.projects")
  };

  const getViewInBMAction = (): BookingDetailItemAction | null => {
    if (!canViewInBookingModule) {
      return null;
    }
    if (source === DetailsPanelSource.BookingModuleWorkforce) {
      return showInProjectsTab;
    } else {
      const retentionPeriodOverDate = utc(profile.suspendedAt).add(
        config?.profile?.suspended_retention_days ?? 0,
        "days"
      );
      const isProfileSuspendedAndRetentionPeriodIsOver = profile.suspendedAt
        ? utc().isSameOrAfter(retentionPeriodOverDate)
        : false;
      return isProfileSuspendedAndRetentionPeriodIsOver ? null : showInWorkforceTab;
    }
  };

  const actions: BookingDetailItemAction[] = compact([
    getViewInBMAction(),
    ...(!isSuspended
      ? [
          {
            icon: "open" as const,
            onClick: () => navigate(`/profiles/${profile?.id}`),
            tooltip: t("bookingModule:details.openPage.profile")
          }
        ]
      : [])
  ]);

  return (
    <div className={css.actionableContainer}>
      <BookingDetailItem
        label={t("bookingModule:details.workforceMember")}
        actions={!isEmpty(actions) ? actions : undefined}
        value={
          isBMEnabled && canOpenDetailsPanelOfType(DetailsPanelType.Profile) && !isSuspended ? (
            <Button
              type="button"
              kind="text"
              onClick={() => openProfileSidePanel(profile.id)}
              title={getProfileName(profile)}
              className={css.workforceMemberButton}
            >
              {profileNameElement}
            </Button>
          ) : (
            profileNameElement
          )
        }
      />
    </div>
  );
};
